import {
  LayoutDashboard,
  Package,
  Tags,
  Award,
  ShoppingCart,
  Users,
  Boxes,
  Warehouse,
  Image,
  GalleryHorizontal,
  Wallet,
  Megaphone,
  Bell,
  Settings,
  ScrollText,
  BarChart3,
  Server,
  type LucideIcon,
} from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';

export interface AdminNavItem {
  label: string;
  icon: LucideIcon;
  /** Present only for entries this milestone actually implements -- omit rather than link to a route that doesn't exist yet. */
  route?: string;
  /** User needs at least one of these roles for the entry to render at all -- backend independently re-enforces this regardless (Part 15). */
  anyRole: string[];
  /** `true` for a future-milestone entry: rendered greyed-out, non-interactive, with a "Soon" marker -- never a fake complete page (Part 15/Part 20 "deferred pages"). */
  future?: boolean;
}

/**
 * Single source of truth for the admin sidebar. `Dashboard` plus every
 * Catalog/Inventory/Media (Milestone 2), Orders/Customers/Payments
 * (Milestone 3), and Marketing/Notifications/Settings/Audit Logs/Reports/
 * System operations (Milestone 4) page is now active. Refunds have no
 * separate nav entry -- the refunds list is reached from the Payments
 * page (a refund is always scoped to a payment), matching how order
 * detail already surfaces refund history inline rather than as its own
 * top-level section. "Marketing" routes to the Coupons list -- rewards
 * and cross-placement banners are reached from within that area's own
 * navigation, same pattern as Inventory/Warehouses both being active
 * siblings. "Content" routes to Homepage Banners (Content > Homepage
 * Banners), a distinct, hero-specific banner manager scoped to the
 * storefront's `home_hero` placement -- deliberately separate from
 * "Marketing"'s general cross-placement banner/campaign manager.
 */
export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  {
    label: 'Dashboard',
    icon: LayoutDashboard,
    route: ROUTE_NAMES.adminDashboard,
    anyRole: ['ADMIN', 'STAFF'],
  },
  { label: 'Products', icon: Package, route: ROUTE_NAMES.adminProducts, anyRole: ['ADMIN', 'STAFF'] },
  { label: 'Categories', icon: Tags, route: ROUTE_NAMES.adminCategories, anyRole: ['ADMIN', 'STAFF'] },
  { label: 'Brands', icon: Award, route: ROUTE_NAMES.adminBrands, anyRole: ['ADMIN', 'STAFF'] },
  { label: 'Orders', icon: ShoppingCart, route: ROUTE_NAMES.adminOrders, anyRole: ['ADMIN', 'STAFF'] },
  { label: 'Customers', icon: Users, route: ROUTE_NAMES.adminCustomers, anyRole: ['ADMIN', 'STAFF'] },
  { label: 'Inventory', icon: Boxes, route: ROUTE_NAMES.adminInventory, anyRole: ['ADMIN', 'STAFF'] },
  { label: 'Warehouses', icon: Warehouse, route: ROUTE_NAMES.adminWarehouses, anyRole: ['ADMIN', 'STAFF'] },
  { label: 'Media', icon: Image, route: ROUTE_NAMES.adminMedia, anyRole: ['ADMIN', 'STAFF'] },
  {
    label: 'Content',
    icon: GalleryHorizontal,
    route: ROUTE_NAMES.adminHomepageBanners,
    anyRole: ['ADMIN', 'STAFF'],
  },
  { label: 'Payments', icon: Wallet, route: ROUTE_NAMES.adminPayments, anyRole: ['ADMIN', 'STAFF'] },
  { label: 'Marketing', icon: Megaphone, route: ROUTE_NAMES.adminCoupons, anyRole: ['ADMIN', 'STAFF'] },
  { label: 'Notifications', icon: Bell, route: ROUTE_NAMES.adminNotifications, anyRole: ['ADMIN', 'STAFF'] },
  { label: 'Reports', icon: BarChart3, route: ROUTE_NAMES.adminReports, anyRole: ['ADMIN', 'STAFF'] },
  { label: 'Audit Logs', icon: ScrollText, route: ROUTE_NAMES.adminAuditLogs, anyRole: ['ADMIN'] },
  { label: 'System', icon: Server, route: ROUTE_NAMES.adminSystem, anyRole: ['ADMIN', 'STAFF'] },
  { label: 'Settings', icon: Settings, route: ROUTE_NAMES.adminSettings, anyRole: ['ADMIN', 'STAFF'] },
];
